import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { ArrowLeft, ArrowRight, Play, Clock, Rows3, Columns2, ChevronUp, ChevronDown } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { saveUserProgress, loadUserProgress, markSolutionAsViewed } from '../utils/progress';
import problemsData from '../data/problems.json';
import solutionsData from '../data/solutions.json';
import circuitProblemTestCases from '../data/circuitProblemTestCases.json';
import circuitSimulatorHarness from '../data/circuitSimulatorHarness.json';
import {
  assembleSimulatorRunnable,
  editorBodyToQuirkImportSnippet,
} from '../utils/simulatorCircuitHarness';
import { getInitialEditorCode } from '../utils/problemEditorScaffold';
import { parseGraderResult, stripGraderChannelFromDisplay } from '../utils/graderResult';
import { executePythonCode } from '../utils/executeCode';
import { useHintEditor } from '../context/HintEditorContext';
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';
import {
  MEASUREMENT_ORDERING_HINT,
  measurementKetLabel,
} from '../constants/measurementProbabilities';
import './ProblemDetail.css';

const DESC_PANEL_MIN_PCT = 20;
const DESC_PANEL_MAX_PCT = 60;
const LAYOUT_NARROW_MQ = '(max-width: 960px)';

const describeSimulatorState = (numWires, probabilities) => {
  if (!Number.isFinite(numWires) || numWires < 1 || !Array.isArray(probabilities)) {
    return '';
  }
  const activeStates = probabilities
    .map((probability, index) => ({ index, probability: Number(probability) || 0 }))
    .filter(({ probability }) => probability > 1e-6)
    .map(
      ({ index, probability }) =>
        `${measurementKetLabel(index, numWires)}: ${(probability * 100).toFixed(2)}%`
    );
  return `${numWires} qubit${numWires === 1 ? '' : 's'}; measurement probabilities: ${
    activeStates.join(', ') || 'no active states reported'
  }`;
};

/** When conceptInfo is shown beside the heading, drop the duplicate h2 from authored HTML. */
const stripLeadingStatementHeading = (html) =>
  html.replace(
    /<div class="problem-content">\s*<h2>\s*Problem Statement\s*<\/h2>\s*/i,
    '<div class="problem-content problem-content--external-statement">'
  );

const EmbeddedMeasurementProbabilities = ({ numWires, probabilities }) => {
  if (!probabilities || probabilities.length === 0) {
    return (
      <div className="embedded-probability-chart embedded-probability-chart--empty">
        <div className="embedded-probability-chart__title-row">
          <p className="embedded-probability-chart__title">Measurement Probabilities</p>
          <p className="embedded-probability-chart__ordering">{MEASUREMENT_ORDERING_HINT}</p>
        </div>
        <p className="embedded-probability-chart__hint">
          Build a circuit to see the probability of each computational basis state.
        </p>
      </div>
    );
  }

  return (
    <div className="embedded-probability-chart">
      <div className="embedded-probability-chart__heading">
        <div className="embedded-probability-chart__title-row">
          <p className="embedded-probability-chart__title">Measurement Probabilities</p>
          <p className="embedded-probability-chart__ordering">{MEASUREMENT_ORDERING_HINT}</p>
        </div>
        <p className="embedded-probability-chart__subtitle">
          {numWires} qubit{numWires === 1 ? '' : 's'} · {probabilities.length} state
          {probabilities.length === 1 ? '' : 's'}
        </p>
      </div>
      <div className="embedded-probability-chart__bars">
        {probabilities.map((prob, i) => {
          const clamped = Math.min(Math.max(prob, 0), 1);
          const percent = (clamped * 100).toFixed(clamped > 0.0999 ? 1 : 2);
          const isActive = clamped > 1e-6;
          return (
            <div className="embedded-probability-chart__row" key={i}>
              <span className="embedded-probability-chart__label">{measurementKetLabel(i, numWires)}</span>
              <div className="embedded-probability-chart__track">
                <div
                  className="embedded-probability-chart__fill"
                  style={{ width: `${clamped * 100}%` }}
                />
              </div>
              <span className={`embedded-probability-chart__value${isActive ? ' embedded-probability-chart__value--active' : ''}`}>
                {percent}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function requestSimulatorQiskitPython(contentWindow, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!contentWindow) {
      reject(new Error('Simulator iframe is still loading.'));
      return;
    }

    const requestId =
      (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
      `q_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const onMsg = (ev) => {
      if (ev.source !== contentWindow || !ev.data || ev.data.requestId !== requestId) {
        return;
      }
      if (ev.data.type !== 'quirk-qiskit-code-response') {
        return;
      }
      window.removeEventListener('message', onMsg);
      clearTimeout(timer);
      if (ev.data.error) {
        reject(new Error(ev.data.error));
      } else {
        resolve(ev.data.code || '');
      }
    };

    window.addEventListener('message', onMsg);
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMsg);
      reject(new Error('Timed out retrieving circuit code from the simulator.'));
    }, timeoutMs);

    try {
      contentWindow.postMessage(
        { type: 'quirk-request-qiskit-code', requestId },
        window.location.origin
      );
    } catch (e) {
      clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      reject(e);
    }
  });
}

function requestSimulatorLoadQiskit(contentWindow, snippet, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!contentWindow) {
      reject(new Error('Simulator iframe is still loading.'));
      return;
    }
    if (typeof snippet !== 'string' || snippet.trim() === '') {
      reject(new Error('No editor circuit to load into the simulator.'));
      return;
    }

    const requestId =
      (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
      `q_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const onMsg = (ev) => {
      if (ev.source !== contentWindow || !ev.data || ev.data.requestId !== requestId) {
        return;
      }
      if (ev.data.type !== 'quirk-load-qiskit-code-response') {
        return;
      }
      window.removeEventListener('message', onMsg);
      clearTimeout(timer);
      if (ev.data.error) {
        reject(new Error(ev.data.error));
      } else {
        resolve();
      }
    };

    window.addEventListener('message', onMsg);
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMsg);
      reject(new Error('Timed out loading editor circuit into the simulator.'));
    }, timeoutMs);

    try {
      contentWindow.postMessage(
        { type: 'quirk-load-qiskit-code', requestId, code: snippet },
        window.location.origin
      );
    } catch (e) {
      clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      reject(e);
    }
  });
}

const ProblemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentProblem = problemsData[id] || problemsData["1"];

  const descriptionHtml = useMemo(() => {
    const html = currentProblem.description || '';
    return currentProblem.conceptInfo ? stripLeadingStatementHeading(html) : html;
  }, [currentProblem]);

  // Navigation logic
  const problemIds = Object.keys(problemsData);
  const currentProblemIndex = problemIds.indexOf(id);
  const prevProblemId = currentProblemIndex > 0 ? problemIds[currentProblemIndex - 1] : null;
  const nextProblemId = currentProblemIndex < problemIds.length - 1 ? problemIds[currentProblemIndex + 1] : null;

  const [code, setCode] = useState(() => getInitialEditorCode(currentProblem));
  const {
    setActiveWorkspace,
    setEditorCode,
    setSimulatorCode,
    setSimulatorState,
  } = useHintEditor();
  const [pythonOutput, setPythonOutput] = useState('');
  const [simulatorOutput, setSimulatorOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [executionTarget, setExecutionTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [user, setUser] = useState(null);
  const [completedProblems, setCompletedProblems] = useState({});
  const [viewedSolutions, setViewedSolutions] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [lastOutput, setLastOutput] = useState('');
  const [submissions, setSubmissions] = useState(currentProblem.submissions);
  const [showSolution, setShowSolution] = useState(false);
  const [showSolutionConfirmation, setShowSolutionConfirmation] = useState(false);
  const [showConceptInfo, setShowConceptInfo] = useState(false);
  const [editorFrameTab, setEditorFrameTab] = useState('python');
  const [useVerticalLayout, setUseVerticalLayout] = useState(false);
  const [descWidthPct, setDescWidthPct] = useState(42);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(LAYOUT_NARROW_MQ).matches : false
  );
  const [simulatorIframeKey, setSimulatorIframeKey] = useState(0);
  const [simulatorIframeMounted, setSimulatorIframeMounted] = useState(false);
  const [simulatorProbabilities, setSimulatorProbabilities] = useState({ numWires: 0, probabilities: [] });
  const [simulatorToolbarCollapsed, setSimulatorToolbarCollapsed] = useState(true);
  const simulatorIframeRef = useRef(null);
  const monacoEditorRef = useRef(null);
  const monacoApiRef = useRef(null);
  const problemLayoutRef = useRef(null);
  const modalBlocksSimulatorUpdatesRef = useRef(false);
  const simulatorSnapshotTimerRef = useRef(null);
  const initialSimulatorSeedRef = useRef(null);
  const simulatorSeededForProblemRef = useRef(false);

  const layoutStacked = useVerticalLayout || isNarrow;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(LAYOUT_NARROW_MQ);
    const handler = () => setIsNarrow(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const problemLayoutStyle = useMemo(() => {
    if (layoutStacked) {
      /* Content-sized rows: full description visible, tight gap to editor/simulator */
      return {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto auto',
      };
    }
    return {
      gridTemplateColumns: `minmax(280px, min(${descWidthPct}%, ${DESC_PANEL_MAX_PCT}%)) minmax(0, 1fr)`,
    };
  }, [layoutStacked, descWidthPct]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [descWidthPct, layoutStacked, editorFrameTab]);

  const handleDescColResizePointerDown = useCallback(
    (e) => {
      if (layoutStacked) return;
      e.preventDefault();
      const el = e.currentTarget;
      const layout = problemLayoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      const startX = e.clientX;
      const startPct = descWidthPct;
      const w = rect.width;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const next = startPct + (dx / w) * 100;
        const clamped = Math.min(DESC_PANEL_MAX_PCT, Math.max(DESC_PANEL_MIN_PCT, next));
        setDescWidthPct(Math.round(clamped * 10) / 10);
      };
      const onUp = (ev) => {
        try {
          el.releasePointerCapture(ev.pointerId);
        } catch (_) {
          /* noop */
        }
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      el.setPointerCapture(e.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [layoutStacked, descWidthPct]
  );

  const SIMULATOR_EMBED_PATH = '/quantum_simulator/out/quirk.html?embed=1&toolbar=closed';

  const simulatorHarnessRow = circuitSimulatorHarness[id];
  const simulatorRunEnabled =
    Boolean(simulatorHarnessRow) && simulatorHarnessRow.mode !== 'unsupported';

  const seedSimulatorFromInitialPreset = useCallback(
    (contentWindow) => {
      if (!contentWindow || !simulatorRunEnabled) return;
      if (simulatorSeededForProblemRef.current) return;
      const snippet = initialSimulatorSeedRef.current;
      if (!snippet) return;
      requestSimulatorLoadQiskit(contentWindow, snippet)
        .then(() => {
          simulatorSeededForProblemRef.current = true;
        })
        .catch(() => {
          /* one-time preset seed is best-effort */
        });
    },
    [simulatorRunEnabled]
  );
  const simulatorRunDisableHint =
    simulatorHarnessRow?.mode === 'unsupported'
      ? simulatorHarnessRow.reason ||
        'Simulator Run is unavailable for this problem.'
      : !simulatorHarnessRow
        ? 'Simulator grading is not configured for this problem.'
        : '';

  const parseSubmissions = (subs) => {
    if (typeof subs === 'string' && subs.toUpperCase().endsWith('K')) {
      return parseFloat(subs) * 1000;
    }
    return parseInt(subs, 10);
  };

  const formatSubmissions = (subs) => {
    if (subs >= 10000) {
      return (subs / 1000).toFixed(1) + 'K';
    }
    return subs.toString();
  };

  const handleShowSolution = () => {
    setShowSolutionConfirmation(true);
  };

  const handleConfirmSolution = () => {
    if (user) {
      markSolutionAsViewed(user.uid, id);
      setViewedSolutions(prev => ({ ...prev, [id]: true })); // Update local state
    }
    setShowSolution(true);
    setShowSolutionConfirmation(false);
  };

  const handleCancelSolution = () => {
    setShowSolutionConfirmation(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const { completedProblems, viewedSolutions } = await loadUserProgress(user.uid);
        setCompletedProblems(completedProblems);
        setViewedSolutions(viewedSolutions);
        if (completedProblems[id]?.completedAt) {
          setShowSolution(true);
        }
      } else {
        setCompletedProblems({});
        setViewedSolutions({});
      }
    });

    return () => unsubscribe();
  }, [id]);

  // Update completion status when user changes or when we complete a problem
  useEffect(() => {
    if (user && completedProblems[id]) {
      // Force a re-render to ensure the completion status is visible
      const timer = setTimeout(() => {
        setCompletedProblems(prev => ({ ...prev }));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, completedProblems, id]);

  useEffect(() => {
    const newProblem = problemsData[id] || problemsData["1"];
    const initialCode = getInitialEditorCode(newProblem);
    const entryName = circuitSimulatorHarness[id]?.entry || 'create_circuit';
    setCode(initialCode);
    initialSimulatorSeedRef.current = editorBodyToQuirkImportSnippet(initialCode, entryName);
    simulatorSeededForProblemRef.current = false;
    setPythonOutput('');
    setSimulatorOutput('');
    setExecutionTarget(null);
    setShowSolution(false); // Reset solution visibility
    setShowConceptInfo(false);
    setSubmissions(newProblem.submissions);
    setEditorFrameTab('python');
    setActiveWorkspace('python');
    setSimulatorCode('');
    setSimulatorState('');
    setSimulatorIframeMounted(false);
    setSimulatorIframeKey((k) => k + 1);
  }, [id, setActiveWorkspace, setSimulatorCode, setSimulatorState]);

  useEffect(() => {
    setEditorCode(code);
    return () => setEditorCode('');
  }, [code, setEditorCode]);

  useEffect(() => {
    setActiveWorkspace(editorFrameTab);
    if (editorFrameTab === 'simulator') {
      setSimulatorIframeMounted(true);
    }
  }, [editorFrameTab, setActiveWorkspace]);

  useEffect(() => {
    modalBlocksSimulatorUpdatesRef.current =
      showSuccessModal ||
      showErrorModal ||
      showConceptInfo ||
      showSolutionConfirmation;
  }, [showSuccessModal, showErrorModal, showConceptInfo, showSolutionConfirmation]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'quirk-probabilities') {
        return;
      }
      if (modalBlocksSimulatorUpdatesRef.current) {
        return;
      }
      const frame = simulatorIframeRef.current;
      if (!frame || event.source !== frame.contentWindow) {
        return;
      }
      const probabilities = Array.isArray(data.probabilities) ? data.probabilities : [];
      setSimulatorProbabilities({
        numWires: data.numWires,
        probabilities,
      });
      setSimulatorState(describeSimulatorState(data.numWires, probabilities));

      if (simulatorSnapshotTimerRef.current) {
        clearTimeout(simulatorSnapshotTimerRef.current);
      }
      simulatorSnapshotTimerRef.current = setTimeout(() => {
        requestSimulatorQiskitPython(frame.contentWindow)
          .then(setSimulatorCode)
          .catch(() => {
            /* The simulator may still be applying the latest edit. */
          });
      }, 250);
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      if (simulatorSnapshotTimerRef.current) {
        clearTimeout(simulatorSnapshotTimerRef.current);
      }
    };
  }, [setSimulatorCode, setSimulatorState]);

  useEffect(() => {
    setSimulatorProbabilities({ numWires: 0, probabilities: [] });
  }, [id, simulatorIframeKey]);

  // Style headings in problem content
  useEffect(() => {
    const styleHeadings = () => {
      const problemContent = document.querySelector('.problem-content');
      if (!problemContent) return;

      const headings = problemContent.querySelectorAll('h2, h3, h4, strong');
      headings.forEach((heading) => {
        const text = heading.textContent.toLowerCase();
        if (text.includes('problem statement') || text.includes('problem') || text.includes('task') || text.includes('test case') || text.includes('test')) {
          heading.style.color = '#9a8a7a';
        }
      });
    };

    // Run after a short delay to ensure DOM is updated
    const timer = setTimeout(styleHeadings, 100);
    return () => clearTimeout(timer);
  }, [activeTab, id]);

  useEffect(() => {
    const body = document.body;
    if (showSuccessModal || showErrorModal || showConceptInfo || showSolutionConfirmation) {
      body.classList.add('modal-open');
    } else {
      body.classList.remove('modal-open');
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      body.classList.remove('modal-open');
    };
  }, [showSuccessModal, showErrorModal, showConceptInfo, showSolutionConfirmation]);

  // Defer Monaco layout when the problem/editor grid toggles. (Monaco uses
  // automaticLayout: false — see Editor options — so we avoid an internal
  // ResizeObserver fighting the CSS grid.)
  useEffect(() => {
    const editor = monacoEditorRef.current;
    if (!editor) return undefined;
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      if (cancelled) return;
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          editor.layout();
        } catch (_) {
          /* ignore */
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [useVerticalLayout]);

  // Viewport resizes: layout Monaco without synthetic events (grid toggle is handled above).
  useEffect(() => {
    const scheduleLayout = () => {
      const ed = monacoEditorRef.current;
      if (!ed) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            ed.layout();
          } catch (_) {
            /* ignore */
          }
        });
      });
    };
    window.addEventListener('resize', scheduleLayout);
    return () => window.removeEventListener('resize', scheduleLayout);
  }, []);

  const checkCompletion = (output) => {
    const testcaseConfig = circuitProblemTestCases[id];
    if (testcaseConfig?.grading === 'harnessJson') {
      return parseGraderResult(output) === true;
    }

    const { expectedOutput } = currentProblem;

    const normalizeStrings = (str) => {
      if (str === null || str === undefined) {
        return '';
      }
      return str.toString().replace(/\s/g, '');
    };

    const normalizeForThreshold = (str) => {
      if (str === null || str === undefined) {
        return '';
      }
      return str.toString().trim().replace(/\s+/g, ' ');
    };

    if (output === null || output === undefined) {
      return false;
    }

    // Version 1: Simple String Match
    if (typeof expectedOutput === 'string') {
      return normalizeStrings(output) === normalizeStrings(expectedOutput);
    }

    // Version 2: Multiple Acceptable Outputs (modified to handle array of objects for thresholds)
    if (Array.isArray(expectedOutput)) {
      // Check if it's an array of threshold objects
      const isThresholdArray = expectedOutput.every(e => typeof e === 'object' && e !== null && e.operator && e.value !== undefined && e.description !== undefined);

      if (isThresholdArray) {
        // All conditions in the array must be met
        return expectedOutput.every(expectedItem => {
          const { operator, value, description } = expectedItem;

          // Find the line in the output that matches the description
          const outputLines = output.split('\n').map(line => normalizeForThreshold(line));
          
          let matchedOutputValue = null;

          for (const line of outputLines) {
            if (line.startsWith(normalizeForThreshold(description))) {
              const outputMatches = line.match(/([\d.-]+)$/);
              if (outputMatches) {
                matchedOutputValue = parseFloat(outputMatches[0]);
                break;
              }
            }
          }

          if (matchedOutputValue === null) return false; // Description not found or value not extracted

          switch (operator) {
            case '>': return matchedOutputValue > value;
            case '<': return matchedOutputValue < value;
            case '>=': return matchedOutputValue >= value;
            case '<=': return matchedOutputValue <= value;
            case '==': return matchedOutputValue === value;
            case '~': return Math.abs(matchedOutputValue - value) < 0.1; // For "around" comparison
            default: return false;
          }
        });
      } else {
        // Original logic for multiple acceptable string outputs
        const normalizedOutput = normalizeStrings(output);
        return expectedOutput.some(e => normalizeStrings(e) === normalizedOutput);
      }
    }

    // Version 3: Threshold Comparison
    if (typeof expectedOutput === 'object' && expectedOutput !== null && !Array.isArray(expectedOutput)) {
      const normalizedOutput = normalizeForThreshold(output);
      const { operator, value, description } = expectedOutput;

      const outputMatches = normalizedOutput.match(/([\d.-]+)$/);
      if (!outputMatches) return false;
      const outputValue = parseFloat(outputMatches[0]);

      const outputText = normalizedOutput.replace(/([\d.-]+)$/, '').trim();
      const descriptionText = normalizeForThreshold(description).replace(/[<>=]+\s*[\d.]+$/, '').trim();
      
      if (outputText !== descriptionText) {
        if (!outputText.endsWith(descriptionText)) {
          return false;
        }
      }

      switch (operator) {
        case '>': return outputValue > value;
        case '<': return outputValue < value;
        case '>=': return outputValue >= value;
        case '<=': return outputValue <= value;
        case '==': return outputValue === value;
        default: return false;
      }
    }

    // Fallback
    const normalizedOutput = normalizeForThreshold(output);
    const normalizedExpected = normalizeForThreshold(expectedOutput.toString());
    return normalizedOutput === normalizedExpected;
  };

  const extractExpectedOutputFromDescription = (problemDescription) => {
    try {
      const marker = '<strong>Expected Output:</strong><br/>';
      const startIndex = problemDescription.indexOf(marker);

      if (startIndex !== -1) {
        const fromMarker = problemDescription.substring(startIndex + marker.length);
        const endIndex = fromMarker.indexOf('</div>');
        let rawOutput = (endIndex !== -1) ? fromMarker.substring(0, endIndex) : fromMarker;
        
        const withNewlines = rawOutput.replace(/<br\s*\/?>/gi, ' ');
        const noTags = withNewlines.replace(/<[^>]+>/g, '');
        
        const cleaned = noTags
          .split('\n')
          .map(line => line.trim())
          .join(' ')

        return cleaned.trim();
      }
      
      return "Check the problem description for expected output";
    } catch (error) {
      console.error('Error extracting expected output:', error);
      return "Check the problem description for expected output";
    }
  };

  // Function to get expected output for display
  const getExpectedOutput = (problemId) => {
    const testcaseConfig = circuitProblemTestCases[problemId];
    if (Array.isArray(testcaseConfig?.displayLines) && testcaseConfig.displayLines.length > 0) {
      return testcaseConfig.displayLines.join('\n');
    }

    const problem = problemsData[problemId];
    if (!problem) return "Check the problem description for expected output";

    return extractExpectedOutputFromDescription(problem.description);
  };

  const cancelExecution = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      const target = executionTarget || 'python';
      const message = 'Execution cancelled by user.';
      if (target === 'simulator') setSimulatorOutput(message);
      else setPythonOutput(message);
      setExecutionTarget(null);
    }
  };

  /** POST assembled Python to sandbox; shared by Run Code and Simulator Run */
  const executePythonSubmission = async (runnableCode, target = 'python') => {
    const setTabOutput = target === 'simulator' ? setSimulatorOutput : setPythonOutput;
    const currentSubmissions = parseSubmissions(submissions);
    setSubmissions(formatSubmissions(currentSubmissions + 1));
    setIsLoading(true);
    setExecutionTarget(target);
    setTabOutput('Starting Python execution...');

    let messageInterval;

    try {
      const controller = new AbortController();
      setAbortController(controller);
      const timeoutId = setTimeout(() => controller.abort(), 90000);

      const loadingMessages = [
        'Starting Python execution...',
        'Loading Qiskit libraries...',
        'Setting up quantum simulator...',
        'Running your quantum circuit...',
        'Processing results...'
      ];

      let messageIndex = 0;
      messageInterval = setInterval(() => {
        if (messageIndex < loadingMessages.length - 1) {
          messageIndex++;
          setTabOutput(loadingMessages[messageIndex]);
        }
      }, 2000);

      const result = await executePythonCode(runnableCode, { signal: controller.signal });

      clearTimeout(timeoutId);
      clearInterval(messageInterval);
      setAbortController(null);
      const displayOutput = stripGraderChannelFromDisplay(result);
      setTabOutput(displayOutput);
      setLastOutput(displayOutput);

      if (checkCompletion(result)) {
        setShowSuccessModal(true);

        if (user && !completedProblems[id]?.completedAt && !viewedSolutions[id]) {
          setCompletedProblems((prev) => ({
            ...prev,
            [id]: { completedAt: new Date(), solution: code }
          }));

          saveUserProgress(user.uid, id, code).then(() => {
            window.dispatchEvent(new CustomEvent('progressUpdated'));
          }).catch((error) => {
            console.error('Failed to save progress:', error);
          });
        }
      } else {
        setShowErrorModal(true);
      }
    } catch (error) {
      if (messageInterval) clearInterval(messageInterval);
      setAbortController(null);

      if (error.name === 'AbortError') {
        setTabOutput('Execution timed out after 90 seconds. Please try again with simpler code or check your internet connection.');
      } else if (error.message.includes('fetch')) {
        setTabOutput(
          'Network error: cannot reach the execute service at http://localhost:8080. Start it with npm run dev in services/execute, then try again.'
        );
      } else {
        setTabOutput(`❌ Error: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
      setExecutionTarget(null);
    }
  };

  const runCode = async () => {
    const hiddenPre =
      typeof problemsData[id]?.hiddenHarnessPrefix === 'string'
        ? problemsData[id].hiddenHarnessPrefix
        : '';
    const hiddenSuf =
      typeof problemsData[id]?.hiddenHarnessSuffix === 'string'
        ? problemsData[id].hiddenHarnessSuffix
        : '';
    const runnableCode =
      hiddenPre === '' && hiddenSuf === '' ? code : `${hiddenPre}${code}${hiddenSuf}`;
    await executePythonSubmission(runnableCode, 'python');
  };

  const runSimulatorCircuit = async () => {
    if (!simulatorRunEnabled) {
      setSimulatorOutput(simulatorRunDisableHint);
      return;
    }
    if (!simulatorIframeMounted || !simulatorIframeRef.current?.contentWindow) {
      setSimulatorOutput('Open the simulator tab and wait for it to finish loading.');
      return;
    }
    try {
      setSimulatorOutput('Retrieving circuit as Qiskit (same as Retrieve Code)...');
      const exportedPy = await requestSimulatorQiskitPython(
        simulatorIframeRef.current.contentWindow
      );
      const runnableCode = assembleSimulatorRunnable(
        exportedPy,
        simulatorHarnessRow,
        currentProblem
      );
      await executePythonSubmission(runnableCode, 'simulator');
    } catch (e) {
      setSimulatorOutput(`❌ Error: ${e.message || String(e)}`);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#7a9a9a';
      case 'medium': return '#9a8a7a';
      case 'hard': return '#9a7a7a';
      default: return '#8a8a8a';
    }
  };

  return (
    <div className="problem-workspace">
      {/* Header with back button */}
      <div className="problem-detail-nav">
        <button
          type="button"
          className="problem-nav-btn"
          onClick={() => navigate('/problems')}
        >
          <ArrowLeft size={20} strokeWidth={2} aria-hidden />
          Back to Problems
        </button>

        <div className="problem-detail-nav__steppers">
          <button
            type="button"
            className="problem-nav-btn"
            onClick={() => navigate(`/problems/${prevProblemId}`)}
            disabled={!prevProblemId}
          >
            <ArrowLeft size={20} strokeWidth={2} aria-hidden />
            Previous
          </button>
          <button
            type="button"
            className="problem-nav-btn"
            onClick={() => navigate(`/problems/${nextProblemId}`)}
            disabled={!nextProblemId}
          >
            Next
            <ArrowRight size={20} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>

        {/* Problem Header */}
        <div className="card problem-hero">
          {viewedSolutions[id] && !completedProblems[id]?.completedAt && (
            <div className="problem-notice problem-notice--solution-viewed" role="status">
              <div className="problem-notice__body">
                <p className="problem-notice__title">Completion disabled</p>
                <p className="problem-notice__text">
                  You opened the official solution for this problem, so it can no longer be marked as
                  completed. You can still run code and use the simulator.
                </p>
              </div>
            </div>
          )}
          <div className="problem-hero__top">
            <div className="problem-hero__main">
              <div className="problem-hero__title-row">
                <h1 className="problem-hero__title">
                  {currentProblem.title}
                </h1>
                {completedProblems[id]?.completedAt && (
                  <span className="problem-badge problem-badge--done">
                    ✓ Completed
                  </span>
                )}
              </div>
              <div className="problem-hero__meta">
                <span
                  className="problem-difficulty-pill"
                  style={{
                    background: `${getDifficultyColor(currentProblem.difficulty)}22`,
                    color: getDifficultyColor(currentProblem.difficulty),
                  }}
                >
                  {currentProblem.difficulty}
                </span>
                <span className="problem-hero__category">{currentProblem.category}</span>
              </div>
            </div>

            <div className="problem-hero__time">
              <Clock size={16} strokeWidth={2} aria-hidden />
              <span>{currentProblem.time}</span>
            </div>
          </div>

        {/* Problem Stats */}
        <div className="problem-hero-stats">
          <div className="problem-hero-stats__item">
            <div className="problem-hero-stats__value problem-hero-stats__value--a">{currentProblem.acceptance}</div>
            <div className="problem-hero-stats__label">Acceptance</div>
          </div>
          <div className="problem-hero-stats__item">
            <div className="problem-hero-stats__value problem-hero-stats__value--b">{submissions}</div>
            <div className="problem-hero-stats__label">Submissions</div>
          </div>
          <div className="problem-hero-stats__item">
            <div className="problem-hero-stats__value problem-hero-stats__value--c">#{id}</div>
            <div className="problem-hero-stats__label">Problem ID</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        ref={problemLayoutRef}
        className={`problem-layout${useVerticalLayout ? ' problem-layout--vertical' : ''}${layoutStacked ? ' problem-layout--stacked' : ''}${layoutStacked && editorFrameTab === 'simulator' ? ' problem-layout--simulator-grow' : ''}`}
        style={problemLayoutStyle}
      >
        {/* Left Panel - Problem Description */}
        <div className="problem-layout__left card problem-layout__left--with-splitter">
          <div className="problem-layout__left-header">
            <div className="problem-glass-tabstrip problem-glass-tabstrip--flush" role="tablist" aria-label="Problem content">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'description'}
                className={`problem-inline-tabs__btn${activeTab === 'description' ? ' problem-inline-tabs__btn--active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                Description
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'solution'}
                className={`problem-inline-tabs__btn${activeTab === 'solution' ? ' problem-inline-tabs__btn--active' : ''}`}
                onClick={() => setActiveTab('solution')}
              >
                Solution
              </button>
            </div>
            <button
              type="button"
              className="problem-layout-mode-toggle"
              onClick={() => setUseVerticalLayout((v) => !v)}
              aria-pressed={useVerticalLayout}
              aria-label={useVerticalLayout ? 'Switch to horizontal layout' : 'Switch to vertical layout'}
              title={useVerticalLayout ? 'Switch to horizontal layout' : 'Switch to vertical layout'}
            >
              {useVerticalLayout ? (
                <>
                  <Columns2 size={18} aria-hidden />
                  <span>Horizontal layout</span>
                </>
              ) : (
                <>
                  <Rows3 size={18} aria-hidden />
                  <span>Vertical layout</span>
                </>
              )}
            </button>
          </div>

          <div className="left-panel-content problem-desc-content">
            {activeTab === 'description' ? (
              <>
                {currentProblem.conceptInfo && (
                  <div className="problem-statement-header">
                    <h2 className="problem-statement-header__title">Problem Statement</h2>
                    <div className="problem-concept-info-bar">
                      <button
                        type="button"
                        className="problem-concept-info-trigger"
                        onClick={() => setShowConceptInfo(true)}
                        aria-haspopup="dialog"
                        aria-expanded={showConceptInfo}
                      >
                        <span className="problem-concept-info-trigger__icon" aria-hidden>
                          ○
                        </span>
                        <span className="problem-concept-info-trigger__label">
                          {currentProblem.conceptInfo.triggerLabel ||
                            currentProblem.conceptInfo.title}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                <div
                  dangerouslySetInnerHTML={{
                    __html: descriptionHtml,
                  }}
                />
              </>
            ) : (
              user ? (
                (completedProblems[id]?.completedAt || viewedSolutions[id] || showSolution) ? (
                  <div className="solution-code-container">
                    <pre className="solution-code">
                      {solutionsData[id]?.solution}
                    </pre>
                  </div>
                ) : (
                  <div className="problem-reveal-solution">
                    <h3 className="problem-solutions-gate__title">View Solution</h3>
                    <button
                      type="button"
                      className="problem-nav-btn problem-solutions-gate__cta"
                      onClick={handleShowSolution}
                    >
                      Show Solution
                    </button>
                  </div>
                )
              ) : (
                <div className="problem-solutions-gate">
                  <h3 className="problem-solutions-gate__title">Sign In to View Solutions</h3>
                  <button
                    type="button"
                    className="problem-nav-btn problem-solutions-gate__cta"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('showAuthModal'));
                    }}
                  >
                    Sign In to Continue
                  </button>
                </div>
              )
            )}
          </div>
          {!layoutStacked && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize description panel width"
              title="Drag to resize description panel"
              className="problem-desc-splitter problem-desc-splitter--col"
              onPointerDown={handleDescColResizePointerDown}
            />
          )}
        </div>

        {/* Right Panel — Python editor + simulator tabs */}
        <div
          className={`card problem-layout__right${editorFrameTab === 'simulator' ? ' problem-layout__right--simulator-active' : ''}`}
        >
          <div className="problem-layout__right-header">
            <div className="problem-glass-tabstrip problem-editor-tabstrip problem-glass-tabstrip--flush">
              <div className="problem-inline-tabs" role="tablist" aria-label="Editor view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={editorFrameTab === 'python'}
                  className={`problem-inline-tabs__btn${editorFrameTab === 'python' ? ' problem-inline-tabs__btn--active' : ''}`}
                  onClick={() => setEditorFrameTab('python')}
                >
                  Python Code Editor
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={editorFrameTab === 'simulator'}
                  className={`problem-inline-tabs__btn${editorFrameTab === 'simulator' ? ' problem-inline-tabs__btn--active' : ''}`}
                  onClick={() => setEditorFrameTab('simulator')}
                >
                  Run Simulator
                </button>
              </div>
            </div>
            {editorFrameTab === 'python' && (
              <div className="problem-layout__right-header-actions">
                {isLoading && executionTarget === 'python' ? (
                  <button type="button" className="problem-run-btn problem-run-btn--cancel" onClick={cancelExecution}>
                    <span aria-hidden>✕</span>
                    Cancel
                  </button>
                ) : (
                  <button type="button" className="sign-in-btn" onClick={runCode} disabled={isLoading}>
                    <Play size={16} strokeWidth={2} aria-hidden />
                    Run Code
                  </button>
                )}
              </div>
            )}
            {editorFrameTab === 'simulator' && (
              <div className="problem-layout__right-header-actions">
                {isLoading && executionTarget === 'simulator' ? (
                  <button type="button" className="problem-run-btn problem-run-btn--cancel" onClick={cancelExecution}>
                    <span aria-hidden>✕</span>
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="sign-in-btn"
                    onClick={runSimulatorCircuit}
                    disabled={!simulatorRunEnabled || !simulatorIframeMounted || isLoading}
                    title={
                      simulatorRunEnabled
                        ? 'Run graded checks on the Qiskit from your circuit (same source as Retrieve Code)'
                        : simulatorRunDisableHint
                    }
                  >
                    <Play size={16} strokeWidth={2} aria-hidden />
                    Run
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            className={`problem-editor-column${editorFrameTab !== 'python' ? ' problem-layout__panel--hidden' : ''}`}
            aria-hidden={editorFrameTab !== 'python'}
          >
              <div className="problem-editor-monaco-wrap">
                <Editor
                  height="100%"
                  defaultLanguage="python"
                  theme="problem-workspace"
                  beforeMount={(monaco) => {
                    monacoApiRef.current = monaco;
                    monaco.editor.defineTheme('problem-workspace', {
                      base: 'vs',
                      inherit: true,
                      rules: [
                        { token: 'comment', foreground: '64748b', fontStyle: 'italic' },
                        { token: 'keyword', foreground: '4a7d8a', fontStyle: 'bold' },
                        { token: 'string', foreground: 'a16207' },
                        { token: 'number', foreground: '0f766e' },
                        { token: 'regexp', foreground: 'b45309' },
                      ],
                      colors: {
                        'editor.background': '#f4f6f8',
                        'editor.foreground': '#1d1d1f',
                        'editorLineNumber.foreground': '#94a3b8',
                        'editorLineNumber.activeForeground': '#64748b',
                        'editor.selectionBackground': '#6b8a9a33',
                        'editor.inactiveSelectionBackground': '#6b8a9a22',
                        'editor.selectionHighlightBackground': '#6b8a9a18',
                        'editorCursor.foreground': '#5a7d8a',
                        'editorWhitespace.foreground': '#cbd5e150',
                        'editorIndentGuide.background': '#e2e8f0',
                        'editorIndentGuide.activeBackground': '#cbd5e1',
                        'editor.lineHighlightBackground': '#eef1f5',
                        'editorBracketMatch.background': '#6b8a9a22',
                        'editorBracketMatch.border': '#6b8a9a55',
                        'scrollbar.shadow': '#00000000',
                        'scrollbarSlider.background': '#94a3b855',
                        'scrollbarSlider.hoverBackground': '#64748b66',
                        'scrollbarSlider.activeBackground': '#64748b88',
                      },
                    });
                  }}
                  value={code}
                  onChange={setCode}
                  onMount={(editor) => {
                    monacoEditorRef.current = editor;
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        try {
                          editor.layout();
                        } catch (_) {
                          /* ignore */
                        }
                      });
                    });
                  }}
                  options={{
                    fontSize: 14,
                    fontFamily:
                      "ui-monospace, 'SF Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, monospace",
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    automaticLayout: false,
                    padding: { top: 10, bottom: 10 },
                    cursorBlinking: 'smooth',
                    smoothScrolling: true,
                  }}
                />
              </div>

              {(pythonOutput || (isLoading && executionTarget === 'python')) && (
                <div className="problem-output-panel">
                  <div className="problem-output-panel__header">
                    {isLoading && executionTarget === 'python' && (
                      <div className="problem-output-panel__spinner" aria-hidden />
                    )}
                    {isLoading && executionTarget === 'python' ? 'Executing...' : 'Output:'}
                  </div>
                  <div className="problem-output-panel__body">{pythonOutput}</div>
                </div>
              )}
          </div>

          <div
            className={`problem-simulator-column${editorFrameTab !== 'simulator' ? ' problem-layout__panel--hidden' : ''}`}
            aria-hidden={editorFrameTab !== 'simulator'}
          >
              <div className="problem-simulator-stack">
              <div className="problem-simulator-scroll" aria-label="Quantum circuit simulator">
                <div className="problem-simulator-chrome-bar">
                  <span className="problem-simulator-chrome-label">Simulator tools</span>
                  <button
                    type="button"
                    className="problem-simulator-chrome-toggle"
                    aria-expanded={!simulatorToolbarCollapsed}
                    aria-label={
                      simulatorToolbarCollapsed ? 'Show simulator toolbar' : 'Hide simulator toolbar'
                    }
                    title={simulatorToolbarCollapsed ? 'Show toolbar' : 'Hide toolbar'}
                    onClick={() => {
                      const next = !simulatorToolbarCollapsed;
                      setSimulatorToolbarCollapsed(next);
                      try {
                        simulatorIframeRef.current?.contentWindow?.postMessage(
                          { type: 'quirk-set-toolbar-collapsed', collapsed: next },
                          window.location.origin
                        );
                      } catch (_) {
                        /* ignore */
                      }
                    }}
                  >
                    {simulatorToolbarCollapsed ? (
                      <ChevronDown size={14} strokeWidth={2} aria-hidden />
                    ) : (
                      <ChevronUp size={14} strokeWidth={2} aria-hidden />
                    )}
                  </button>
                </div>
                <div className="problem-simulator-iframe-wrap">
                  {simulatorIframeMounted && (
                    <iframe
                      ref={simulatorIframeRef}
                      key={simulatorIframeKey}
                      title="Quantum circuit simulator"
                      className="problem-simulator-shell__iframe"
                      src={`${SIMULATOR_EMBED_PATH}&v=${simulatorIframeKey}`}
                      onLoad={() => {
                        const frameWindow = simulatorIframeRef.current?.contentWindow;
                        try {
                          frameWindow?.postMessage(
                            { type: 'quirk-set-toolbar-collapsed', collapsed: simulatorToolbarCollapsed },
                            window.location.origin
                          );
                        } catch (_) {
                          /* ignore */
                        }
                        seedSimulatorFromInitialPreset(frameWindow);
                      }}
                    />
                  )}
                </div>
              </div>
              {simulatorIframeMounted && (
                <EmbeddedMeasurementProbabilities
                  numWires={simulatorProbabilities.numWires}
                  probabilities={simulatorProbabilities.probabilities}
                />
              )}
              </div>
              {(simulatorOutput || (isLoading && executionTarget === 'simulator')) && (
                <div className="problem-output-panel">
                  <div className="problem-output-panel__header">
                    {isLoading && executionTarget === 'simulator' && (
                      <div className="problem-output-panel__spinner" aria-hidden />
                    )}
                    {isLoading && executionTarget === 'simulator' ? 'Executing...' : 'Output:'}
                  </div>
                  <div className="problem-output-panel__body">{simulatorOutput}</div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        problemTitle={currentProblem.title}
      />

      {/* Error Modal */}
      <ErrorModal 
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        problemTitle={currentProblem.title}
        output={lastOutput}
        expectedOutput={getExpectedOutput(id)}
      />

      {showConceptInfo && currentProblem.conceptInfo && (
        <div
          className="problem-concept-info-overlay"
          role="presentation"
          onClick={() => setShowConceptInfo(false)}
        >
          <div
            className="problem-concept-info-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="concept-info-title"
            aria-describedby="concept-info-body"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="problem-concept-info-dialog__title" id="concept-info-title">
              {currentProblem.conceptInfo.title}
            </h2>
            <p className="problem-concept-info-dialog__body" id="concept-info-body">
              {currentProblem.conceptInfo.body}
            </p>
            <div className="problem-concept-info-dialog__actions">
              <button
                type="button"
                className="sign-in-btn"
                onClick={() => setShowConceptInfo(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Solution Confirmation Modal */}
      {showSolutionConfirmation && (
        <div
          className="problem-solution-confirm-overlay"
          role="presentation"
          onClick={handleCancelSolution}
        >
          <div
            className="problem-solution-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="solution-confirm-title"
            aria-describedby="solution-confirm-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="problem-solution-confirm__title" id="solution-confirm-title">
              Reveal solution?
            </h2>
            <p className="problem-solution-confirm__lede" id="solution-confirm-desc">
              This will prevent you from receiving credit for this problem.
            </p>
            <div className="problem-solution-confirm__actions">
              <button type="button" className="problem-nav-btn" onClick={handleCancelSolution}>
                Cancel
              </button>
              <button type="button" className="sign-in-btn" onClick={handleConfirmSolution}>
                Show Solution
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemDetail;
